"use client";

import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { DeferHydrate } from "./DeferHydrate";
import { PROJECT } from "@/lib/site";

const LocationMap = dynamic(
  () => import("./LocationMap").then((m) => m.LocationMap),
  {
    ssr: false,
    loading: () => <div className="h-full w-full animate-pulse bg-surface" />,
  },
);

export function Location() {
  const t = useTranslations();
  const distances = t.raw("distances") as { place: string; dist: string }[];

  return (
    <section
      id="location"
      className="mx-auto max-w-[1280px] px-[clamp(20px,4vw,40px)] py-[clamp(72px,9vw,120px)]"
    >
      <div className="grid items-stretch gap-[clamp(36px,5vw,72px)] lg:grid-cols-[1.05fr_0.95fr]">
        <div data-reveal className="min-w-0">
          <p className="mb-[18px] text-xs uppercase tracking-[.2em] text-accent">
            {t("locKicker")}
          </p>
          <h2 className="text-[clamp(28px,3.4vw,44px)] leading-[1.12]">
            {t("locTitle")}
          </h2>
          <p className="mt-[18px] max-w-[52ch] text-[16px] leading-[1.7] font-light text-muted">
            {t("locSub")}
          </p>
          <ul className="mt-[34px] list-none border-t border-ink/[.14] p-0">
            {distances.map((d) => (
              <li
                key={d.place}
                className="flex items-baseline justify-between gap-5 border-b border-ink/[.14] py-[15px]"
              >
                <span className="min-w-0 text-[15px] text-ink">{d.place}</span>
                <span className="font-display text-right text-[15.5px] font-medium text-accent">
                  {d.dist}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[12.5px] leading-[1.6] text-subtle">
            {t("locNote")}
          </p>
        </div>
        <div data-reveal className="flex h-full min-w-0 flex-col gap-3">
          <div className="relative min-h-[320px] w-full flex-1 overflow-hidden border border-ink/[.12] bg-surface">
            {/* Mapbox GL - самый тяжёлый скрипт страницы (около 570 мс исполнения).
                Монтируем карту только при подъезде к вьюпорту, иначе она съедала
                главный поток сразу после гидрации. */}
            <DeferHydrate rootMargin="200px" className="h-full w-full">
              <LocationMap />
            </DeferHydrate>
          </div>
          <div className="flex flex-wrap items-center justify-start gap-3">
            <a
              href={`https://www.google.com/maps?cid=${PROJECT.location.googleCid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap rounded-[3px] border border-accent px-[18px] py-[11px] text-center text-[13px] font-medium text-accent transition-colors hover:bg-accent hover:text-white"
            >
              {t("mapCta")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
